import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldCheck, Trash2, Lock, Plus } from "lucide-react";

const Row = ({ email, sub, onRemove, busy, testId }) => (
  <div className="flex items-center gap-3 px-4 py-3" data-testid={testId}>
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-medium">{email}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
    {onRemove ? (
      <button onClick={onRemove} disabled={busy} aria-label={`Remove ${email}`} data-testid={`${testId}-remove`}
        className="flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-red-500/10 hover:text-red-500 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
      </button>
    ) : <Lock className="h-4 w-4 text-muted-foreground" />}
  </div>
);

export const FreeAccessManager = () => {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [showBuiltIn, setShowBuiltIn] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/free-access"); setData(data); }
    catch { toast.error("Couldn't load free-access list"); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setAdding(true);
    try {
      const { data: res } = await api.post("/admin/free-access", { email: email.trim() });
      toast.success(`${res.email} now has free access`);
      setEmail("");
      await load();
    } catch (err) {
      toast.error(err?.response?.status === 422 ? "Enter a valid email" : "Couldn't add — try again");
    } finally { setAdding(false); }
  };

  const remove = async (em) => {
    if (!window.confirm(`Remove free access for ${em}? They'll hit the paywall next time.`)) return;
    setRemoving(em);
    try { await api.delete(`/admin/free-access/${encodeURIComponent(em)}`); toast.success("Access removed"); await load(); }
    catch { toast.error("Couldn't remove — try again"); }
    finally { setRemoving(null); }
  };

  return (
    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5" data-testid="free-access-manager">
      <div className="flex items-center gap-2 text-sm font-semibold text-emerald-500"><ShieldCheck className="h-4 w-4" /> Free access list</div>
      <p className="mt-1 text-xs text-muted-foreground">Emails here skip the paywall (no subscription). Not admin. Takes effect within ~30 seconds — no re-publish.</p>
      <form onSubmit={add} className="mt-4 flex gap-2">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" data-testid="free-access-email-input" />
        <Button type="submit" disabled={adding} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="free-access-add-btn">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" />Add</>}
        </Button>
      </form>
      {!data ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-emerald-500" /></div>
      ) : (
        <>
          <div className="mt-4 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card" data-testid="free-access-list">
            {data.emails.length === 0 && <div className="px-4 py-4 text-sm text-muted-foreground" data-testid="free-access-empty">No emails added yet.</div>}
            {data.emails.map((d) => (
              <Row key={d.email} email={d.email} testId={`free-access-row-${d.email}`} busy={removing === d.email}
                sub={d.added_at ? `Added ${new Date(d.added_at).toLocaleDateString()}` : null} onRemove={() => remove(d.email)} />
            ))}
          </div>
          {data.built_in.length > 0 && (
            <button onClick={() => setShowBuiltIn((v) => !v)} className="mt-3 text-xs font-semibold text-muted-foreground hover:text-foreground" data-testid="free-access-builtin-toggle">
              {showBuiltIn ? "Hide" : "Show"} {data.built_in.length} built-in accounts (locked)
            </button>
          )}
          {showBuiltIn && (
            <div className="mt-2 divide-y divide-border overflow-hidden rounded-xl border border-border bg-card" data-testid="free-access-builtin-list">
              {data.built_in.map((em) => <Row key={em} email={em} testId={`free-access-builtin-${em}`} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
};
